import React, { useEffect, useRef, useState } from "react";
import StoriLogo from "./StoriLogo";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLeadModal } from "./LeadModalContext";

// NOTE: sub-link hrefs marked with `#` are placeholders — replace when real URLs are ready.
const NAV_LINKS = [
  { label: "For Marketers", href: "/marketers", route: true },
  { label: "For Events", href: "/events", route: true },
  { label: "Why Us", href: "/features", route: true },
  { label: "Pricing", href: "/pricing", route: true },
  {
    label: "Learn",
    children: [
      { label: "Help Center", href: "#" },
      { label: "Social Media", href: "#" },
      { label: "About Stori", href: "/about", route: true },
      { label: "Contact Us", action: "contact" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // desktop
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const dropdownRef = useRef(null);
  const { openModal } = useLeadModal();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close desktop dropdown on outside click
  useEffect(() => {
    if (openDropdown === null) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpenDropdown(null);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openDropdown]);

  const handleSubAction = (item) => {
    if (item.action === "contact") {
      openModal("demo_request");
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 backdrop-blur-md ${
        scrolled
          ? "bg-stori-bg/85 backdrop-blur-xl border-b border-stori-border"
          : "bg-stori-bg/30"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <StoriLogo className="h-8 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" ref={dropdownRef}>
          {NAV_LINKS.map((l) => {
            const testId = `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
            const isActive = l.route && location.pathname === l.href;
            const linkClass = `text-[18px] transition-colors ${
              isActive ? "text-white" : "text-stori-muted hover:text-white"
            }`;

            // Dropdown parent
            if (l.children) {
              const isOpen = openDropdown === l.label;
              return (
                <div key={l.label} className="relative">
                  <button
                    type="button"
                    data-testid={testId}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    onClick={() => setOpenDropdown(isOpen ? null : l.label)}
                    className={`${linkClass} inline-flex items-center gap-1`}
                  >
                    {l.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`${testId}-dropdown`}
                      role="menu"
                      className="absolute right-0 mt-3 w-56 rounded-xl border border-stori-border bg-stori-surface1/95 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.5)] overflow-hidden"
                    >
                      <ul className="py-2">
                        {l.children.map((c) => {
                          const subTestId = `nav-sublink-${c.label
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`;
                          const className =
                            "block px-4 py-2.5 text-sm text-stori-muted hover:bg-stori-cyan/10 hover:text-white transition-colors";
                          if (c.action) {
                            return (
                              <li key={c.label} role="none">
                                <button
                                  type="button"
                                  role="menuitem"
                                  data-testid={subTestId}
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    handleSubAction(c);
                                  }}
                                  className={`${className} w-full text-left`}
                                >
                                  {c.label}
                                </button>
                              </li>
                            );
                          }
                          if (c.route) {
                            return (
                              <li key={c.label} role="none">
                                <Link
                                  role="menuitem"
                                  to={c.href}
                                  data-testid={subTestId}
                                  onClick={() => setOpenDropdown(null)}
                                  className={className}
                                >
                                  {c.label}
                                </Link>
                              </li>
                            );
                          }
                          return (
                            <li key={c.label} role="none">
                              <a
                                role="menuitem"
                                href={c.href}
                                data-testid={subTestId}
                                onClick={() => setOpenDropdown(null)}
                                className={className}
                              >
                                {c.label}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            }

            // Plain link
            return l.route ? (
              <Link key={l.href} to={l.href} data-testid={testId} className={linkClass}>
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} data-testid={testId} className={linkClass}>
                {l.label}
              </a>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://hub.selfiecam.ai/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-signin-link"
            className="text-sm text-stori-muted hover:text-white px-3 py-2"
          >
            Sign in
          </a>
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="navbar-cta-button"
            className="text-sm font-medium bg-stori-cyan text-black px-4 py-2 rounded-md hover:bg-stori-cyanHover transition-colors"
          >
            Book a demo
          </button>
        </div>

        <button
          aria-label="Open menu"
          data-testid="navbar-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stori-border bg-stori-bg" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV_LINKS.map((l) => {
              if (l.children) {
                const isOpen = openMobileGroup === l.label;
                return (
                  <div key={l.label} className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setOpenMobileGroup(isOpen ? null : l.label)}
                      className="flex items-center justify-between text-stori-muted hover:text-white text-base py-1"
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                      aria-expanded={isOpen}
                    >
                      {l.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <ul className="mt-2 ml-3 pl-3 border-l border-stori-border flex flex-col gap-3">
                        {l.children.map((c) => {
                          const subTestId = `mobile-sublink-${c.label
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`;
                          if (c.action) {
                            return (
                              <li key={c.label}>
                                <button
                                  type="button"
                                  data-testid={subTestId}
                                  onClick={() => {
                                    setOpen(false);
                                    handleSubAction(c);
                                  }}
                                  className="text-sm text-stori-muted hover:text-white text-left"
                                >
                                  {c.label}
                                </button>
                              </li>
                            );
                          }
                          if (c.route) {
                            return (
                              <li key={c.label}>
                                <Link
                                  to={c.href}
                                  data-testid={subTestId}
                                  onClick={() => setOpen(false)}
                                  className="text-sm text-stori-muted hover:text-white"
                                >
                                  {c.label}
                                </Link>
                              </li>
                            );
                          }
                          return (
                            <li key={c.label}>
                              <a
                                href={c.href}
                                data-testid={subTestId}
                                onClick={() => setOpen(false)}
                                className="text-sm text-stori-muted hover:text-white"
                              >
                                {c.label}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              }
              return l.route ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="text-stori-muted hover:text-white text-base"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-stori-muted hover:text-white text-base"
                >
                  {l.label}
                </a>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openModal("demo_request");
              }}
              className="mt-2 inline-flex justify-center bg-stori-cyan text-black px-4 py-3 rounded-md font-medium"
              data-testid="mobile-cta-button"
            >
              Book a demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
